import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { DateRangePickerContext } from './context/DateRangePickerContext';
import { Trigger } from './components/Trigger';
import { DatePickerPopover } from './components/Popover';
import { GranularityToggle } from './components/GranularityToggle';
import { DailyPicker } from './pickers/DailyPicker';
import { WeeklyPicker } from './pickers/WeeklyPicker';
import { MonthlyPicker } from './pickers/MonthlyPicker';
import { getRangeDisplayName } from './utils/rangeDisplay';
import type { DateRangePickerProps } from './DateRangePicker.types';
import { cn } from 'utils/classNamesTailwind';

export function DateRangePicker({
  value,
  onChange,
  onCancel,
  granularity,
  onGranularityChange,
  allowedGranularities,
  presetOptions,
  showPresets = true,
  caption,
  disabled,
  emptyMessage = 'datepicker.empty.default',
  hideDateInputs = false,
  hideButtons = false,
  disabledDates = () => false,
  onValidate,
  minDate,
  maxDate,
  fixedNumberOfMonth,
  isComparison = false,
  mainRangeDuration,
  className,
  fullWidth = false,
}: DateRangePickerProps) {
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!open) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onCancel?.();
  };

  const displayValue = getRangeDisplayName(
    value.range,
    { defaultValue: emptyMessage, presets: presetOptions },
    isComparison,
    intl
  );

  const contextValue = useMemo(
    () => ({
      value,
      onChange,
      onClose: handleClose,
      granularity,
      onGranularityChange,
      allowedGranularities,
      presetOptions,
      showPresets,
      caption,
      disabled: disabled ?? false,
      emptyMessage,
      hideDateInputs,
      hideButtons,
      disabledDates,
      onValidate,
      minDate,
      maxDate,
      fixedNumberOfMonth,
      isComparison,
      mainRangeDuration,
    }),

    [
      value,
      onChange,
      granularity,
      onGranularityChange,
      allowedGranularities,
      presetOptions,
      showPresets,
      caption,
      disabled,
      emptyMessage,
      hideDateInputs,
      hideButtons,
      disabledDates,
      onValidate,
      minDate,
      maxDate,
      fixedNumberOfMonth,
      isComparison,
      mainRangeDuration,
    ]
  );

  return (
    <div className={cn(fullWidth ? 'block w-full' : 'inline-block', className)}>
      <Trigger
        caption={caption}
        displayValue={displayValue}
        open={open}
        disabled={disabled}
        onClick={handleOpen}
        fullWidth={fullWidth}
      />

      <DatePickerPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <DateRangePickerContext.Provider value={contextValue}>
          <div className="h-fit">
            {allowedGranularities && onGranularityChange && (
              <div className="px-4 pt-4 pb-3 border-b border-gray-200">
                <GranularityToggle />
              </div>
            )}

            {granularity === 'monthly' ? (
              <MonthlyPicker />
            ) : granularity === 'weekly' ? (
              <WeeklyPicker />
            ) : (
              <DailyPicker />
            )}
          </div>
        </DateRangePickerContext.Provider>
      </DatePickerPopover>
    </div>
  );
}
